import {
    flattenLogicNodes,
    type LogicActionNode,
    type LogicConditionNode,
    type TradeLogicDiagnostic,
    type TradeLogicDraft,
    type TradeLogicJsonValue,
    type TradeLogicNodeId
} from '../model';
import type { ContextFeatureRegistryFeatureRefValidationResult } from '@/types/appRouter';
import type {
    TradeLogicRegistryActionTemplateDto,
    TradeLogicRegistryConditionTemplateDto,
    TradeLogicRegistryFeatureDto,
    TradeLogicRegistryManifestDto,
    TradeLogicRegistrySchemaFieldDto
} from './types';

interface ValidateTradeLogicDraftAgainstRegistryInput {
    draft: TradeLogicDraft;
    manifest: TradeLogicRegistryManifestDto | null | undefined;
    featuresByKey: Record<string, TradeLogicRegistryFeatureDto>;
    actionTemplatesByKey: Record<string, TradeLogicRegistryActionTemplateDto>;
    conditionTemplatesByKey: Record<string, TradeLogicRegistryConditionTemplateDto>;
    registryManifestFingerprint?: string | null;
}

const VALUELESS_OPERATORS = new Set(['empty', 'not_empty']);
const RANGE_OPERATORS = new Set(['between', 'outside']);

export const validateTradeLogicDraftAgainstRegistry = ({
    actionTemplatesByKey,
    conditionTemplatesByKey,
    draft,
    featuresByKey,
    manifest,
    registryManifestFingerprint
}: ValidateTradeLogicDraftAgainstRegistryInput): TradeLogicDiagnostic[] => {
    const diagnostics: TradeLogicDiagnostic[] = [];
    const expectedFingerprint = registryManifestFingerprint ?? manifest?.semanticFingerprint ?? null;

    if (!expectedFingerprint) {
        diagnostics.push(
            createDiagnostic('registry.manifest-missing-fingerprint', 'warning', 'Registry manifest has no semantic fingerprint.')
        );
    } else if (draft.registryManifestFingerprint && draft.registryManifestFingerprint !== expectedFingerprint) {
        diagnostics.push(
            createDiagnostic(
                'registry.manifest-fingerprint-mismatch',
                'warning',
                'Draft was created against a different registry manifest fingerprint.',
                undefined,
                {
                    currentFingerprint: expectedFingerprint,
                    draftFingerprint: draft.registryManifestFingerprint
                }
            )
        );
    }

    if (!draft.rootNode) return diagnostics;

    const registryMismatchSeverity = draft.sourceDefinitionId == null ? 'error' : 'warning';

    for (const { node, path } of flattenLogicNodes(draft.rootNode)) {
        if (node.kind === 'condition') {
            diagnostics.push(
                ...validateConditionNode({
                    conditionTemplatesByKey,
                    featuresByKey,
                    registryMismatchSeverity,
                    node,
                    path
                })
            );
        }

        if (node.kind === 'action') {
            diagnostics.push(
                ...validateActionNode({
                    actionTemplatesByKey,
                    draft,
                    node,
                    path,
                    registryMismatchSeverity
                })
            );
        }
    }

    return diagnostics;
};

export const mapFeatureRefValidationResultToDiagnostics = (
    node: LogicConditionNode | null,
    result: ContextFeatureRegistryFeatureRefValidationResult | null | undefined
): TradeLogicDiagnostic[] => {
    if (!node || !result || result.isValid) return [];

    return result.issues.map((issue, index) =>
        createDiagnostic(`registry.backend-feature-ref.${node.id}.${index}`, 'error', issue.message, node.id, {
            path: issue.path
        })
    );
};

const validateConditionNode = ({
    conditionTemplatesByKey,
    featuresByKey,
    node,
    path,
    registryMismatchSeverity
}: {
    conditionTemplatesByKey: Record<string, TradeLogicRegistryConditionTemplateDto>;
    featuresByKey: Record<string, TradeLogicRegistryFeatureDto>;
    registryMismatchSeverity: 'error' | 'warning';
    node: LogicConditionNode;
    path: number[];
}): TradeLogicDiagnostic[] => {
    const diagnostics: TradeLogicDiagnostic[] = [];

    if (node.templateKey) {
        const template = conditionTemplatesByKey[node.templateKey];

        if (!template) {
            diagnostics.push(
                createDiagnostic(
                    `registry.condition-template-missing.${node.id}`,
                    registryMismatchSeverity,
                    `Condition template "${node.templateKey}" is not present in the loaded registry.`,
                    node.id,
                    { templateKey: node.templateKey },
                    path
                )
            );
        } else {
            diagnostics.push(
                ...validateSchemaValues(
                    template.parameterFields,
                    node.parameters ?? {},
                    node.id,
                    path,
                    'condition-template',
                    registryMismatchSeverity
                )
            );
        }
    }

    if (node.featureRef) {
        const feature = featuresByKey[node.featureRef.featureKey];

        if (!feature) {
            diagnostics.push(
                createDiagnostic(
                    `registry.feature-missing.${node.id}`,
                    registryMismatchSeverity,
                    `Feature "${node.featureRef.featureKey}" is not present in the loaded registry.`,
                    node.id,
                    { featureKey: node.featureRef.featureKey },
                    path
                )
            );
        } else {
            diagnostics.push(...validateFeatureCondition(feature, node, path, registryMismatchSeverity));
        }
    }

    return diagnostics;
};

const validateFeatureCondition = (
    feature: TradeLogicRegistryFeatureDto,
    node: LogicConditionNode,
    path: number[],
    registryMismatchSeverity: 'error' | 'warning'
): TradeLogicDiagnostic[] => {
    const diagnostics: TradeLogicDiagnostic[] = [];

    if (!feature.capabilities.canAuthor) {
        diagnostics.push(
            createDiagnostic(
                `registry.feature-unsupported.${node.id}`,
                'warning',
                feature.capabilities.unsupportedReason ?? `Feature "${feature.featureKey}" is not marked authorable.`,
                node.id,
                { featureKey: feature.featureKey },
                path
            )
        );
    }

    if (node.operator && feature.allowedOperators.length > 0 && !feature.allowedOperators.includes(node.operator)) {
        diagnostics.push(
            createDiagnostic(
                `registry.operator-unsupported.${node.id}`,
                registryMismatchSeverity,
                `Operator "${node.operator}" is not allowed for feature "${feature.featureKey}".`,
                node.id,
                {
                    allowedOperators: feature.allowedOperators,
                    featureKey: feature.featureKey,
                    operator: node.operator
                },
                path
            )
        );
    }

    diagnostics.push(
        ...validateSchemaValues(
            feature.selectorFields,
            node.featureRef?.selectors ?? {},
            node.id,
            path,
            'feature-selector',
            registryMismatchSeverity
        )
    );

    diagnostics.push(...validateFeatureFilters(feature, node, path, registryMismatchSeverity));
    diagnostics.push(...validateConditionValueShape(node, path));

    return diagnostics;
};

const validateFeatureFilters = (
    feature: TradeLogicRegistryFeatureDto,
    node: LogicConditionNode,
    path: number[],
    registryMismatchSeverity: 'error' | 'warning'
): TradeLogicDiagnostic[] => {
    const filters = node.featureRef?.filters ?? [];
    const filterValues = Object.fromEntries(filters.map((filter) => [filter.field, filter.value ?? null]));

    const diagnostics = validateSchemaValues(feature.filterFields, filterValues, node.id, path, 'feature-filter', registryMismatchSeverity);

    const fieldsByKey = Object.fromEntries(feature.filterFields.map((field) => [field.key, field]));

    for (const filter of filters) {
        const field = fieldsByKey[filter.field];

        if (!field) {
            diagnostics.push(
                createDiagnostic(
                    `registry.unknown-filter.${node.id}.${filter.field}`,
                    registryMismatchSeverity,
                    `Filter "${filter.field}" is not present in the loaded registry feature schema.`,
                    node.id,
                    {
                        featureKey: feature.featureKey,
                        filterField: filter.field
                    },
                    path
                )
            );

            continue;
        }

        if (field.operators.length > 0 && !field.operators.includes(filter.operator)) {
            diagnostics.push(
                createDiagnostic(
                    `registry.invalid-filter-operator.${node.id}.${filter.field}`,
                    registryMismatchSeverity,
                    `Operator "${filter.operator}" is not allowed for filter "${filter.field}".`,
                    node.id,
                    {
                        allowedOperators: field.operators,
                        filterField: filter.field,
                        operator: filter.operator
                    },
                    path
                )
            );
        }
    }

    return diagnostics;
};

const validateActionNode = ({
    actionTemplatesByKey,
    draft,
    node,
    path,
    registryMismatchSeverity
}: {
    actionTemplatesByKey: Record<string, TradeLogicRegistryActionTemplateDto>;
    draft: TradeLogicDraft;
    node: LogicActionNode;
    path: number[];
    registryMismatchSeverity: 'error' | 'warning';
}): TradeLogicDiagnostic[] => {
    if (!node.templateKey) return [];

    const template = actionTemplatesByKey[node.templateKey];

    if (!template) {
        return [
            createDiagnostic(
                `registry.action-template-missing.${node.id}`,
                registryMismatchSeverity,
                `Action template "${node.templateKey}" is not present in the loaded registry.`,
                node.id,
                { templateKey: node.templateKey },
                path
            )
        ];
    }

    const diagnostics = validateSchemaValues(
        template.parameterFields,
        node.parameters,
        node.id,
        path,
        'action-template',
        registryMismatchSeverity
    );

    if (template.documentKinds.length > 0 && !template.documentKinds.includes(draft.kind)) {
        diagnostics.push(
            createDiagnostic(
                `registry.action-template-kind.${node.id}`,
                'warning',
                `Action template "${template.templateKey}" is not declared for ${draft.kind} documents.`,
                node.id,
                {
                    documentKinds: template.documentKinds,
                    draftKind: draft.kind,
                    templateKey: template.templateKey
                },
                path
            )
        );
    }

    return diagnostics;
};

const validateSchemaValues = (
    fields: TradeLogicRegistrySchemaFieldDto[],
    values: Record<string, TradeLogicJsonValue>,
    nodeId: TradeLogicNodeId,
    path: number[],
    schemaKind: string,
    invalidValueSeverity: 'error' | 'warning' = 'error'
): TradeLogicDiagnostic[] => {
    const diagnostics: TradeLogicDiagnostic[] = [];

    for (const field of fields) {
        const value = values[field.key];

        if (isEmptyValue(value)) {
            if (field.required) {
                diagnostics.push(
                    createDiagnostic(
                        `registry.required-parameter.${nodeId}.${field.key}`,
                        invalidValueSeverity,
                        `${field.label} is required.`,
                        nodeId,
                        {
                            parameterKey: field.key,
                            schemaKind
                        },
                        path
                    )
                );
            }

            continue;
        }

        if (field.allowedValues.length > 0 && !field.allowedValues.some((allowedValue) => allowedValue === value)) {
            diagnostics.push(
                createDiagnostic(
                    `registry.invalid-allowed-value.${nodeId}.${field.key}`,
                    invalidValueSeverity,
                    `${field.label} must use one of the allowed registry values.`,
                    nodeId,
                    {
                        allowedValues: field.allowedValues,
                        parameterKey: field.key,
                        schemaKind,
                        value
                    },
                    path
                )
            );
        }

        if (!isValueCompatibleWithFieldType(value, field.type)) {
            diagnostics.push(
                createDiagnostic(
                    `registry.invalid-parameter-type.${nodeId}.${field.key}`,
                    invalidValueSeverity,
                    `${field.label} does not match the registry field type "${field.type ?? 'unknown'}".`,
                    nodeId,
                    {
                        expectedType: field.type,
                        parameterKey: field.key,
                        schemaKind,
                        value
                    },
                    path
                )
            );
        }
    }

    return diagnostics;
};

const validateConditionValueShape = (node: LogicConditionNode, path: number[]): TradeLogicDiagnostic[] => {
    if (!node.operator) return [];

    const hasValue = !isEmptyValue(node.value);

    if (VALUELESS_OPERATORS.has(node.operator)) {
        return hasValue
            ? [
                  createDiagnostic(
                      `registry.operator-unexpected-value.${node.id}`,
                      'warning',
                      `Operator "${node.operator}" does not require a comparison value.`,
                      node.id,
                      { operator: node.operator },
                      path
                  )
              ]
            : [];
    }

    if (RANGE_OPERATORS.has(node.operator)) {
        const validRange = Array.isArray(node.value) && node.value.length === 2 && node.value.every((value) => !isEmptyValue(value));

        return validRange
            ? []
            : [
                  createDiagnostic(
                      `registry.operator-invalid-range.${node.id}`,
                      'error',
                      `Operator "${node.operator}" requires a two-value range.`,
                      node.id,
                      { operator: node.operator },
                      path
                  )
              ];
    }

    if (!hasValue) {
        return [
            createDiagnostic(
                `registry.operator-missing-value.${node.id}`,
                'warning',
                `Operator "${node.operator}" usually requires a comparison value.`,
                node.id,
                { operator: node.operator },
                path
            )
        ];
    }

    return [];
};

const isValueCompatibleWithFieldType = (value: TradeLogicJsonValue, type: string | null): boolean => {
    switch (type) {
        case 'boolean':
            return typeof value === 'boolean';
        case 'integer':
            return typeof value === 'number' && Number.isInteger(value);
        case 'number':
        case 'timestamp':
            return typeof value === 'number' && Number.isFinite(value);
        case 'json':
            return true;
        case 'bindingKey':
        case 'duration':
        case 'enum':
        case 'levelKey':
        case 'string':
        case null:
        default:
            return typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean';
    }
};

const isEmptyValue = (value: TradeLogicJsonValue | undefined): boolean => value === undefined || value === null || value === '';

const createDiagnostic = (
    id: string,
    severity: TradeLogicDiagnostic['severity'],
    message: string,
    nodeId?: TradeLogicNodeId,
    metadata?: TradeLogicDiagnostic['metadata'],
    path?: number[]
): TradeLogicDiagnostic => ({
    id,
    severity,
    code: id.split('.').slice(0, 2).join('.'),
    message,
    source: 'registry',
    nodeId,
    metadata,
    path
});
